import type { ReactNode } from 'react';

interface Props {
  shown: number;
  total: number;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
  /** `button`: the ExamDetail `.pg` footer (hairline rule, toggle as a bordered button on the right). */
  variant?: 'inline' | 'button';
  /** Toggle label when collapsed (default "Show all"). */
  showAllLabel?: string;
  /** `button` variant only: static text on the left in place of "Showing N of M" (Students footer). */
  note?: ReactNode;
}

/** "Showing N of M · Show all" footer for short, expandable lists. */
export default function ShowMoreFooter({ shown, total, expanded, onToggle, className = '', variant = 'inline', showAllLabel = 'Show all', note }: Props) {
  if (total <= 0) return null;
  const canToggle = expanded || total > shown;
  if (variant === 'button') {
    return (
      <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4 text-[13px] text-muted ${className}`}>
        {note ?? <span>Showing <span className="font-mono">{shown}</span> of <span className="font-mono">{total}</span></span>}
        {canToggle && (
          <button type="button" onClick={onToggle}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-4 text-sm font-semibold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            {expanded ? 'Show fewer' : showAllLabel}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-2 text-[13px] text-muted ${className}`}>
      <span>Showing <span className="font-mono">{shown}</span> of <span className="font-mono">{total}</span></span>
      {canToggle && (
        <>
          <span aria-hidden="true">·</span>
          <button type="button" onClick={onToggle}
            className="rounded font-semibold text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            {expanded ? 'Show fewer' : showAllLabel}
          </button>
        </>
      )}
    </div>
  );
}
